import React, { ReactNode } from 'react';
import Animation, { animationNameTypes } from '../../foundations/Animations';
import Illustration, { FileExtensions } from '../../foundations/Illustrations';
import { IllustrationNames } from '../../foundations/Illustrations/files';

type ImageT = 'animation' | 'illustration';

type SizeT = 'L' | 'S';

type StateProps = {
  // either 'animation' or 'illustration'
  imageType: ImageT;
  // name of the illustration coming from /foundations/Illustrations/files
  illustrationName?: IllustrationNames;
  // illustration size default is 'S' which is 7rem, otherwise 'L' is 11rem
  illustrationSize?: SizeT;
  // illustration file extension, default is 'png', could be 'svg'
  illustrationFileExtension?: FileExtensions;
  // name of the animation coming from /foundations/Animations
  animationName?: animationNameTypes;
  // animation size default is 'L' which is 11rem, otherwise 'S' is 4rem
  animationSize?: SizeT;
  // loop animation, false by default
  loopAnimation?: boolean;
  // for accessibility
  altText: string;
  // main text - for cases wherein the empty state only has a main and a sub text
  heading?: string;
  // sub text - for cases wherein the empty state only has a main and a sub text
  description?: string;
  // children, can be added after mainText or subText or even override it
  children?: ReactNode;
};

const getAnimation = (
  name: animationNameTypes,
  altText: string,
  animationSize: SizeT,
  loopAnimation: boolean
) => (
  <div className="-mb-m">
    <img className="sr-only" alt={altText} src="" />
    <Animation name={name} size={animationSize} loop={loopAnimation} />
  </div>
);

const getIllustration = (
  name: IllustrationNames,
  altText: string,
  illustrationSize: SizeT,
  illustrationFileExtension: FileExtensions
) => (
  <>
    <img className="sr-only" alt={altText} src="" />
    <Illustration
      name={name}
      className={illustrationSize === 'L' ? 'w-44' : 'w-28'}
      fileExtension={illustrationFileExtension}
    />
  </>
);

const getImage = ({
  imageType,
  altText,
  animationSize,
  illustrationSize,
  animationName,
  loopAnimation,
  illustrationName,
  illustrationFileExtension,
}: {
  imageType: ImageT;
  altText: string;
  animationSize: SizeT;
  illustrationSize: SizeT;
  animationName?: animationNameTypes;
  loopAnimation: boolean;
  illustrationName?: IllustrationNames;
  illustrationFileExtension: FileExtensions;
}) => {
  if (imageType === 'animation' && animationName) {
    return getAnimation(animationName, altText, animationSize, loopAnimation);
  }
  if (imageType === 'illustration' && illustrationName) {
    return getIllustration(
      illustrationName,
      altText,
      illustrationSize,
      illustrationFileExtension
    );
  }
  return getIllustration(
    'emptyUsers',
    altText,
    illustrationSize,
    illustrationFileExtension
  );
};

export const EmptyState = ({
  imageType,
  altText,
  illustrationName,
  illustrationSize = 'S',
  illustrationFileExtension = 'png',
  animationName,
  loopAnimation = false,
  heading,
  description,
  children,
  animationSize = 'L',
}: StateProps) => (
  <div className="mb-m flex flex-col items-center justify-center gap-m">
    {getImage({
      imageType,
      altText,
      animationSize,
      illustrationSize,
      animationName,
      loopAnimation,
      illustrationName,
      illustrationFileExtension,
    })}
    <div className="flex flex-col items-center justify-center gap-xs">
      {heading && (
        <h4 className="text-center font-heading text-h4 font-bold text-regular">
          {heading}
        </h4>
      )}
      {description && (
        <p className="text-center font-body text-body text-subtle">
          {description}
        </p>
      )}
    </div>
    {children}
  </div>
);

export default EmptyState;
